import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { submitLead } from "@/lib/api";

const inputCls =
  "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors";

export default function LeadForm({
  type = "contact",
  submitLabel = "Submit",
  showAddress = true,
  showConsulting = false,
  showBilling = false,
  compact = false,
  showMessage = true,
  initial = {},
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", message: "",
    property_type: "", service_interest: "",
    card_number: "", card_expiry: "", card_cvv: "",
    invoice: "", total: "",
    ...initial,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    if (showBilling && (!form.card_number || !form.card_expiry || !form.card_cvv)) {
      toast.error("Please complete all billing fields.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ type, ...form });
      toast.success("Sent! We'll be in touch shortly.");
      navigate("/thank-you", { state: { lead: type } });
    } catch (err) {
      toast.error("Something went wrong. Please call us at (970) 440-3178.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4" data-testid={`lead-form-${type}`}>
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <label className="overline block mb-2">Name *</label>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="form-name" />
        </div>
        <div>
          <label className="overline block mb-2">Email *</label>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="form-email" />
        </div>
      </div>

      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <label className="overline block mb-2">Phone</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(970) 000-0000" data-testid="form-phone" />
        </div>
        {showAddress && (
          <div>
            <label className="overline block mb-2">Service Address</label>
            <input className={inputCls} value={form.address} onChange={set("address")} placeholder="Street, City, CO" data-testid="form-address" />
          </div>
        )}
      </div>

      {showConsulting && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-2">Property Type</label>
            <select className={inputCls} value={form.property_type} onChange={set("property_type")} data-testid="form-property-type">
              <option value="">Select…</option>
              <option>Home</option>
              <option>Small Business</option>
              <option>Ranch / Large Property</option>
              <option>Multi-Building / Campus</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-2">What do you need?</label>
            <select className={inputCls} value={form.service_interest} onChange={set("service_interest")} data-testid="form-service-interest">
              <option value="">Select…</option>
              <option>WiFi Network Design</option>
              <option>Coverage / Heat-Map Survey</option>
              <option>Mesh Setup</option>
              <option>Troubleshooting & Optimization</option>
              <option>Not sure — need advice</option>
            </select>
          </div>
        </div>
      )}

      {showBilling && (
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2">Card Number *</label>
            <input inputMode="numeric" autoComplete="cc-number" className={inputCls} value={form.card_number} onChange={set("card_number")} placeholder="1234 5678 9012 3456" data-testid="form-card-number" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Expiration *</label>
              <input autoComplete="cc-exp" className={inputCls} value={form.card_expiry} onChange={set("card_expiry")} placeholder="MM/YY" data-testid="form-card-expiry" />
            </div>
            <div>
              <label className="overline block mb-2">CVV *</label>
              <input inputMode="numeric" autoComplete="cc-csc" className={inputCls} value={form.card_cvv} onChange={set("card_cvv")} placeholder="123" data-testid="form-card-cvv" />
            </div>
          </div>
        </div>
      )}

      {showMessage && (
        <div>
          <label className="overline block mb-2">Message</label>
          <textarea rows={compact ? 3 : 4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us a little about what you're looking for…" data-testid="form-message" />
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 bg-cyan-500 text-ink-950 font-display font-bold uppercase tracking-widest px-8 py-4 rounded-xl btn-glow hover:bg-cyan-400 disabled:opacity-60"
        data-testid="form-submit"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
        {loading ? "Sending…" : submitLabel}
      </button>
      <p className="text-xs text-zinc-500 text-center">By submitting you agree to be contacted by CYAN Wireless. No spam, ever.</p>
    </form>
  );
}
